import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  HelpCircle, Search, CreditCard, Calendar, Shield, 
  Users, Clock, MapPin, Phone, Mail, Building2
} from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      id: 'rezervasyon',
      title: 'Rezervasyon İşlemleri',
      icon: Calendar,
      color: 'bg-blue-100 text-blue-700',
      questions: [
        {
          q: 'Nasıl rezervasyon yapabilirim?',
          a: `Rezervasyon yapmak için şu adımları izleyebilirsiniz:
          
1. tesiskirala.com'a giriş yapın veya üye olun
2. Ana sayfadan veya "Tesisler" bölümünden istediğiniz tesisi bulun
3. Tesis detay sayfasında uygun tarih ve saati seçin
4. "Rezervasyon Oluştur" butonuna tıklayın
5. Kullanıcı sözleşmesini onaylayın
6. Ödeme işlemini tamamlayın

Ödeme onaylandıktan sonra rezervasyonunuz kesinleşir ve size e-posta ile bildirim gönderilir.`
        },
        {
          q: 'Rezervasyonumu nasıl iptal edebilirim?',
          a: `Rezervasyon iptali için "Hesabım" bölümünden ilgili rezervasyonu bulup iptal talebinde bulunabilirsiniz.

**İptal Koşulları:**
- Rezervasyon saatinden 24 saat öncesine kadar yapılan iptallerde tam iade yapılır
- 24 saatten az süre kala yapılan iptallerde tesis politikasına göre kesinti uygulanabilir
- Rezervasyon saati geçtikten sonra iptal/iade yapılamamaktadır

Her tesisin kendi iptal politikası olabileceğinden, rezervasyon öncesi tesis detaylarını kontrol etmenizi öneririz.`
        },
        {
          q: 'Rezervasyon için kaç dakika sürem var?',
          a: `Bir saat dilimini seçtiğinizde **5 dakikalık** öncelikli satın alma hakkınız başlar. Bu süre içinde:

- Seçtiğiniz saat başka kullanıcılara gösterilmez
- Ödeme sayfasında geri sayım sayacı görüntülenir
- 5 dakika içinde ödeme yapmazsanız rezervasyon otomatik iptal edilir
- İptal edilen saat tekrar satışa açılır

Bu sistem, gerçekten rezervasyon yapmak isteyen kullanıcıların haklarını korumak için tasarlanmıştır.`
        },
        {
          q: 'Aynı anda birden fazla saat rezerve edebilir miyim?',
          a: `Evet, ardışık saatleri tek seferde rezerve edebilirsiniz. Örneğin:

- 14:00-15:00 ve 15:00-16:00 saatlerini birlikte seçebilirsiniz
- Toplam ücret seçtiğiniz saat sayısına göre hesaplanır
- Kampanya kodları toplam tutara uygulanır

Farklı günler için ayrı rezervasyonlar oluşturmanız gerekmektedir.`
        },
        {
          q: 'Rezervasyonumu değiştirebilir miyim?',
          a: `Mevcut bir rezervasyonun tarih veya saatini doğrudan değiştirmek mümkün değildir. Bunun yerine:

1. Mevcut rezervasyonunuzu iptal edin
2. Yeni tarih/saat için yeni bir rezervasyon oluşturun

İptal koşulları için yukarıdaki "Rezervasyonumu nasıl iptal edebilirim?" sorusuna bakabilirsiniz.`
        }
      ]
    },
    {
      id: 'odeme',
      title: 'Ödeme ve Fiyatlandırma',
      icon: CreditCard,
      color: 'bg-green-100 text-green-700',
      questions: [
        {
          q: 'Hangi ödeme yöntemlerini kabul ediyorsunuz?',
          a: `Şu an için aşağıdaki ödeme yöntemlerini destekliyoruz:

- **Kredi Kartı:** Visa, MasterCard, Troy
- **Banka Kartı:** Tüm banka kartları
- **Online Ödeme:** Güvenli 3D Secure ile

Yakında havale/EFT ve dijital cüzdan seçenekleri de eklenecektir.`
        },
        {
          q: 'Fiyatlara KDV dahil mi?',
          a: `Evet, sitede gösterilen tüm fiyatlara **KDV dahildir**. Ödeme sayfasında gördüğünüz tutar, ödeyeceğiniz son tutardır.

Fatura bilgilerinizi hesap ayarlarınızdan güncelleyebilirsiniz.`
        },
        {
          q: 'Kampanya kodu nasıl kullanırım?',
          a: `Kampanya kodunu kullanmak için:

1. Rezervasyon oluşturma sayfasında "Kampanya Kodu" alanını bulun
2. Kodunuzu girin ve "Uygula" butonuna tıklayın
3. İndirim tutarı otomatik olarak hesaplanır
4. İndirimli fiyat üzerinden ödemenizi tamamlayın

**Not:** Her kampanya kodunun geçerlilik süresi ve kullanım koşulları farklı olabilir. Minimum tutar şartı olan kampanyalar mevcuttur.`
        },
        {
          q: 'Ödeme güvenli mi?',
          a: `Evet, ödeme güvenliğiniz bizim için çok önemlidir:

- **SSL Sertifikası:** Tüm veriler şifrelenerek iletilir
- **3D Secure:** Kart bilgileriniz bankanız tarafından doğrulanır
- **PCI-DSS Uyumlu:** Uluslararası ödeme güvenliği standartlarına uyuyoruz
- **Kart bilgileri saklanmaz:** Kart bilgileriniz sistemimizde tutulmaz

Güvenlik ile ilgili sorularınız için bizimle iletişime geçebilirsiniz.`
        },
        {
          q: 'İade ne zaman hesabıma yansır?',
          a: `İade süreleri ödeme yönteminize göre değişir:

- **Kredi Kartı:** 1-5 iş günü (bankanıza bağlı)
- **Banka Kartı:** 1-3 iş günü
- **Online Ödeme:** 1-3 iş günü

İade işleminizin durumunu "Hesabım" bölümünden takip edebilirsiniz.`
        }
      ]
    },
    {
      id: 'tesisler',
      title: 'Tesisler Hakkında',
      icon: Building2,
      color: 'bg-purple-100 text-purple-700',
      questions: [
        {
          q: 'Hangi tür tesisleri bulabilirim?',
          a: `Platformumuzda çeşitli spor tesisleri bulabilirsiniz:

- **Halı Sahalar:** Futbol, futsal için kapalı/açık sahalar
- **Kapalı Spor Salonları:** Çok amaçlı spor salonları
- **Voleybol Sahaları:** Kapalı ve açık voleybol alanları
- **Basketbol Sahaları:** Kapalı ve açık basketbol kortları
- **Tenis Kortları:** Toprak, sert ve çim kortlar
- **Yüzme Havuzları:** Kapalı ve açık havuzlar

Her şehirde tesis çeşitliliği farklılık gösterebilir.`
        },
        {
          q: 'Tesis özelliklerini nasıl görebilirim?',
          a: `Her tesisin detay sayfasında şu bilgileri bulabilirsiniz:

- **Fotoğraf Galerisi:** Tesisin görüntüleri
- **Özellikler:** Otopark, duş, soyunma odası, kafeterya vb.
- **Konum:** Adres ve Google Maps bağlantısı
- **Fiyat:** Saatlik kiralama ücreti
- **Çalışma Saatleri:** Müsait saat aralıkları
- **Değerlendirmeler:** Diğer kullanıcıların yorumları ve puanları`
        },
        {
          q: 'Tesise nasıl ulaşırım?',
          a: `Her tesisin detay sayfasında:

- Tam adres bilgisi
- Google Maps bağlantısı
- Varsa ulaşım önerileri

yer almaktadır. Rezervasyon onay e-postanızda da konum bilgisi bulunur.`
        },
        {
          q: 'Tesis hakkında şikayetim var, ne yapmalıyım?',
          a: `Tesis ile ilgili şikayetlerinizi birkaç yoldan iletebilirsiniz:

1. **Yorum Yazın:** Rezervasyon sonrası değerlendirme bırakabilirsiniz
2. **İletişim Formu:** Sitemizin iletişim sayfasından bize yazın
3. **E-posta:** info@tesiskirala.com adresine detaylı şikayetinizi iletin

Tüm şikayetler 48 saat içinde değerlendirilir ve size geri dönüş yapılır.`
        }
      ]
    },
    {
      id: 'uyelik',
      title: 'Üyelik ve Hesap',
      icon: Users,
      color: 'bg-orange-100 text-orange-700',
      questions: [
        {
          q: 'Nasıl üye olabilirim?',
          a: `Üyelik için iki seçeneğiniz var:

**1. E-posta ile Kayıt:**
- "Giriş Yap" sayfasından "Kayıt Ol" sekmesine geçin
- Ad, e-posta ve şifre bilgilerinizi girin
- Kullanıcı veya İşletmeci olarak kayıt türünü seçin
- Sözleşmeleri onaylayıp kaydınızı tamamlayın

**2. Google ile Giriş:**
- "Google ile Giriş" butonuna tıklayın
- Google hesabınızla tek tıkla kayıt olun

Üyelik tamamen **ücretsizdir**.`
        },
        {
          q: 'Şifremi unuttum, ne yapmalıyım?',
          a: `Şifrenizi sıfırlamak için:

1. "Giriş Yap" sayfasına gidin
2. "Şifremi Unuttum" bağlantısına tıklayın
3. Kayıtlı e-posta adresinizi girin
4. E-postanıza gelen bağlantıya tıklayın
5. Yeni şifrenizi belirleyin

E-posta gelmezse spam klasörünüzü kontrol edin veya bizimle iletişime geçin.`
        },
        {
          q: 'Hesabımı nasıl silebilirim?',
          a: `Hesap silme işlemi için:

1. info@tesiskirala.com adresine "Hesap Silme Talebi" konulu e-posta gönderin
2. Kayıtlı e-posta adresinizi belirtin
3. Talebiniz 7 iş günü içinde işleme alınır

**Not:** Hesap silindiğinde tüm rezervasyon geçmişiniz ve kişisel verileriniz sistemden kaldırılır. Bu işlem geri alınamaz.`
        },
        {
          q: 'Müşteri mi yoksa İşletmeci mi olarak kayıt olmalıyım?',
          a: `**Müşteri (Kullanıcı) Hesabı:**
- Tesis aramak ve rezervasyon yapmak istiyorsanız
- Spor yapmak için saha kiralamak istiyorsanız

**İşletmeci Hesabı:**
- Kendi spor tesisinizi platforma eklemek istiyorsanız
- Tesis yönetimi ve rezervasyon takibi yapmak istiyorsanız

İşletmeci hesapları admin onayından sonra aktif hale gelir.`
        }
      ]
    },
    {
      id: 'isletmeci',
      title: 'İşletmeciler İçin',
      icon: Building2,
      color: 'bg-teal-100 text-teal-700',
      questions: [
        {
          q: 'Tesisimi nasıl ekleyebilirim?',
          a: `Tesisinizi eklemek için:

1. İşletmeci hesabı ile giriş yapın
2. "İşletmeci Paneli"ne gidin
3. "Yeni Tesis Ekle" butonuna tıklayın
4. Tesis bilgilerini doldurun (ad, tür, adres, fiyat, özellikler)
5. Fotoğraflar yükleyin
6. "Kaydet" ile başvurunuzu gönderin

Başvurunuz admin tarafından incelenir ve onaylandıktan sonra tesisiniz yayına alınır.`
        },
        {
          q: 'Komisyon oranı nedir?',
          a: `TesisKirala.com **%10 komisyon** almaktadır.

**Örnek:**
- Saatlik ücret: 500₺
- Komisyon: 50₺
- Size kalan: 450₺

Komisyon, yalnızca başarılı ve ödemesi tamamlanmış rezervasyonlardan kesilir.`
        },
        {
          q: 'Saatleri nasıl açıp kapatabilirim?',
          a: `İşletmeci panelinizden "Saat Yönetimi" sekmesine giderek:

- Herhangi bir güne tıklayıp o günün saatlerini görüntüleyin
- Kapatmak istediğiniz saate tıklayın (kırmızı olur)
- Açmak için tekrar tıklayın (yeşil olur)

Bu özellik ile bakım, özel etkinlik veya tatil günlerinde saatleri kapatabilirsiniz.`
        },
        {
          q: 'Rezervasyon bildirimlerini nasıl alırım?',
          a: `Her yeni rezervasyonda:

- **E-posta bildirimi:** Kayıtlı e-posta adresinize detaylı bilgi gönderilir
- **Panel bildirimi:** İşletmeci panelinizde anlık olarak görüntülenir

Bildirim ayarlarınızı hesap ayarlarından özelleştirebilirsiniz.`
        }
      ]
    },
    {
      id: 'guvenlik',
      title: 'Güvenlik ve Gizlilik',
      icon: Shield,
      color: 'bg-red-100 text-red-700',
      questions: [
        {
          q: 'Kişisel verilerim güvende mi?',
          a: `Evet, kişisel verilerinizin güvenliği bizim için önceliklidir:

- **KVKK Uyumu:** 6698 sayılı Kişisel Verilerin Korunması Kanunu'na tam uyum
- **SSL Şifreleme:** Tüm veri transferleri şifrelenir
- **Güvenli Sunucular:** Verileriniz güvenli sunucularda saklanır
- **Erişim Kontrolü:** Verilerinize yalnızca yetkili kişiler erişebilir

Detaylı bilgi için KVKK Aydınlatma Metnimizi inceleyebilirsiniz.`
        },
        {
          q: 'Verilerimi nasıl silebilirim?',
          a: `KVKK kapsamında "Unutulma Hakkı"nız bulunmaktadır:

1. info@tesiskirala.com adresine başvurun
2. Kimlik doğrulaması yapılır
3. Verileriniz 30 gün içinde sistemden silinir

Yasal zorunluluklar gereği saklanması gereken veriler hariç tutulur.`
        }
      ]
    }
  ];

  // Filter questions based on search
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <SEOHead
        title="Sık Sorulan Sorular | TesisKirala.com"
        description="TesisKirala.com hakkında merak ettikleriniz. Rezervasyon, ödeme, üyelik, tesis kiralama ve daha fazlası hakkında detaylı cevaplar."
        canonicalPath="/sss"
        showFAQ={true}
      />
      
      {/* Hero */}
      <section className="bg-emerald-800 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="w-16 h-16 text-lime-300 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Sık Sorulan Sorular
          </h1>
          <p className="text-lg text-emerald-100 max-w-2xl mx-auto mb-8">
            Merak ettiğiniz her şeyin cevabı burada. Bulamazsanız bize ulaşın!
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Soru ara... (örn: rezervasyon, ödeme, iptal)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base bg-white border-0"
            />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {faqCategories.map((category) => (
              <a
                key={category.id}
                href={`#${category.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105 ${category.color}`}
              >
                <category.icon className="w-4 h-4" />
                {category.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {searchQuery && filteredCategories.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Sonuç bulunamadı</h3>
              <p className="text-slate-600">
                "{searchQuery}" ile eşleşen soru bulunamadı. Farklı kelimelerle aramayı deneyin.
              </p>
            </div>
          )}

          {(searchQuery ? filteredCategories : faqCategories).map((category) => (
            <div key={category.id} id={category.id} className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category.color}`}>
                  <category.icon className="w-5 h-5" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">{category.title}</h2>
                <Badge variant="secondary" className="ml-auto">
                  {category.questions.length} soru
                </Badge>
              </div>
              
              <Card>
                <CardContent className="p-0">
                  <Accordion type="single" collapsible className="w-full">
                    {category.questions.map((item, index) => (
                      <AccordionItem key={index} value={`${category.id}-${index}`}>
                        <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-slate-50">
                          <span className="font-medium text-slate-900">{item.q}</span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4">
                          <div className="prose prose-slate prose-sm max-w-none">
                            <pre className="whitespace-pre-wrap font-sans text-slate-600 leading-relaxed bg-transparent p-0 m-0">
                              {item.a}
                            </pre>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 bg-emerald-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Sorunuzu bulamadınız mı?
          </h2>
          <p className="text-emerald-100 mb-6">
            Müşteri hizmetlerimiz size yardımcı olmak için hazır.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/iletisim"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-emerald-800 font-medium rounded-lg hover:bg-emerald-50 transition-colors"
            >
              <Mail className="w-5 h-5" />
              Bize Yazın
            </a>
            <a
              href="tel:+905053686091"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-700 text-white font-medium rounded-lg hover:bg-emerald-600 transition-colors"
            >
              <Phone className="w-5 h-5" />
              +90 505 368 60 91
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
